import React, { ReactNode } from 'react';
import {
  TextField as MUITextField,
  TextFieldProps as MUITextFieldProps,
  TextFieldVariants,
  ThemeOptions,
  ThemeProvider,
} from '@mui/material';
import { useContext } from 'react';
import { Icon } from '../../primatives/Icon/Icon';

import { DesignSystemContext } from '../DesignSystemContextProvider/DesignSystemContextProvider';
import { WBBox } from '../..';
import InformationIcon from '../../primatives/InformationIcon/InformationIcon';
import { deepmerge } from 'deepmerge-ts';

export interface TextFieldProps extends Omit<MUITextFieldProps, 'variant'> {
  rightIcon?: string | ReactNode;
  leftIcon?: string | ReactNode;
  success?: boolean;
  error?: boolean;
  helperText?: string;
  multiline?: boolean;
  rows?: string | number;
  maxRows?: string | number;
  variant?: TextFieldVariants;
  rightLabel?: React.ReactNode;
  placeholder?: string;
  infoToolTip?: string;
  ref?: React.Ref<HTMLDivElement>;
}

export const TextField = ({
  success = false,
  error = false,
  type = 'text',
  helperText,
  rightIcon,
  rightLabel,
  leftIcon,
  fullWidth = true,
  multiline = false,
  variant = 'standard',
  rows = 0,
  maxRows = 0,
  placeholder = '',
  inputRef,
  infoToolTip,
  ref,
  ...props
}: TextFieldProps) => {
  const { theme } = useContext(DesignSystemContext);
  const inputTheme: ThemeOptions = {
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            '&.Mui-disabled:before': {
              borderBottomStyle: 'solid !important',
            },
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 100px #000 inset',
              WebkitTextFillColor: 'transparent',
              transitionDelay: '9999s',
              transitionProperty: 'background-color, color',
            },
            '&.Mui-disabled': {
              color: '#000',
            },
          },
          input: {
            '&:-webkit-autofill': {
              transitionDelay: '9999s',
              transitionProperty: 'background-color, color',
            },
            // '::placeholder': {
            //   color: '#000',
            //   opacity: 1,
            // },
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          variant: 'standard',
          shrink: false,
        },
        styleOverrides: {
          root: {
            overflow: 'visible',
            fontSize: '1rem',
            position: 'inherit',
            fontWeight: 700,
            '&.Mui-focused': {
              color: theme.palette.text.primary,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            marginTop: 0,
            '& .MuiOutlinedInput-root': {
              border: 'none',
              borderRadius: '999px',
              backgroundColor: theme.palette.primary.dark,

              '& fieldset': {
                border: 'none', // Removes border
              },
              '&:hover fieldset': {
                border: 'none', // Removes border on hover
              },
              '&.Mui-focused fieldset': {
                border: 'none', // Removes border when focused
              },
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(!ownerState.multiline && {
              maxHeight: theme.spacing(6),
              height: (ownerState.height as string) || theme.spacing(6),
            }),
            ...(ownerState.multiline && {
              height: 'auto',
              padding: '8px 0px',
            }),
            '&.MuiOutlinedInput-root, &.MuiFilledInput-root': {
              // remove underline for outlined and filled variants
              '&:before': {
                content: 'none',
              },
            },
          }),
          underline: ({ ownerState }) => ({
            '&:before': {
              borderBottom: `2px ${'solid'} ${
                ownerState.underlineColor || 'rgba(0, 0, 0, 1)'
              }`,
            },
          }),
        },
      },
    },
  };
  const newTheme = deepmerge(theme, inputTheme);

  let outlinedInputProps: MUITextFieldProps['InputProps'] = {};

  let color:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning' = 'secondary';

  if (error) {
    color = 'error';
  }

  if (success) {
    color = 'success';
  }
  const hasInfoIcon = infoToolTip ? true : false;

  if (helperText && !rightIcon && error) {
    rightIcon = 'CloseCircle';
  }

  if (typeof rightIcon !== 'undefined') {
    outlinedInputProps = {
      ...outlinedInputProps,
      endAdornment:
        typeof rightIcon === 'string' && !props.SelectProps ? (
          <Icon
            name={rightIcon as string}
            color={theme.palette[color].main as string}
            size="medium"
          />
        ) : (
          <>
            {!props.SelectProps ? rightIcon : null}
            {props.InputProps?.endAdornment}
          </>
        ),
    };
  }
  if (typeof leftIcon !== 'undefined') {
    outlinedInputProps = {
      ...outlinedInputProps,
      startAdornment:
        typeof leftIcon === 'string' ? (
          <WBBox mr={2}>
            <Icon
              name={leftIcon as string}
              color={theme.palette.common.white}
              size="small"
            />
          </WBBox>
        ) : (
          leftIcon
        ),
    };
  }
  return (
    <ThemeProvider theme={newTheme}>
      <MUITextField
        ref={ref}
        inputRef={inputRef}
        type={type}
        color={color}
        variant={variant}
        helperText={helperText}
        multiline={multiline}
        rows={rows}
        maxRows={maxRows}
        placeholder={placeholder}
        FormHelperTextProps={{
          sx: {
            marginY: 0,
            color:
              theme.palette[color === 'secondary' ? 'primary' : color].main,
          },
          variant,
        }}
        {...props}
        InputProps={{
          placeholder: placeholder,
          // sx: { padding: '4px 0px' },
          ...props.InputProps,
          ...outlinedInputProps,
        }}
        InputLabelProps={{
          sx: {
            // lineHeight: '40px',
            // marginY: 1,
            marginBottom: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          component: 'div',
          shrink: false,
        }}
        label={
          props.label ? (
            <>
              <WBBox width="100%">
                {props.label}&nbsp;
                <InformationIcon title={infoToolTip} />
              </WBBox>
              <WBBox>{rightLabel}</WBBox>
            </>
          ) : null
        }
        fullWidth={fullWidth}
      />
    </ThemeProvider>
  );
};
